import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
} from "@heroui/react";
import { cn } from "@heroui/react";
import {
  EnvelopeFill,
  PersonFill,
  CheckCircleFill,
} from "react-bootstrap-icons";

import { logger } from "@/lib/secure-logger";
import { ContactModalSchema, ContactModalFormData } from "@/lib/form-schemas";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, customRequest?: string) => void;
  recommendationName: string;
  actionName: string;
  showOnboardingNote?: boolean;
}

export function ContactModal({
  isOpen,
  onClose,
  onSubmit,
  recommendationName,
  actionName,
  showOnboardingNote = false,
}: ContactModalProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm<ContactModalFormData>({
    resolver: zodResolver(ContactModalSchema),
    defaultValues: {
      email: "",
      customRequest: "",
    },
  });

  const email = watch("email");

  const onSubmitForm = async (data: ContactModalFormData) => {
    try {
      // Debug logging - contact submission (no sensitive data)
      logger.debug("Contact form submission initiated", {
        hasEmail: !!data.email.trim(),
        hasCustomRequest: !!data.customRequest?.trim(),
        recommendationName,
        actionName,
        showOnboardingNote,
        emailDomain: data.email.includes("@")
          ? data.email.split("@")[1]
          : "unknown",
      });

      logger.info("ContactModal - Submission data", {
        email: data.email.trim(),
        customRequest: data.customRequest?.trim() || undefined,
        recommendation: recommendationName,
        action: actionName,
        timestamp: new Date().toISOString(),
        userId: localStorage.getItem("crowdsec_user_id"),
        sessionId: localStorage.getItem("crowdsec_session_id"),
        userAgent: navigator.userAgent,
        referrer: document.referrer || "direct",
        currentUrl: window.location.href,
        showOnboardingNote,
        hasCustomRequest: !!data.customRequest?.trim(),
        emailDomain: data.email.includes("@")
          ? data.email.split("@")[1]
          : "unknown",
      });

      // Call the onSubmit callback
      onSubmit(data.email.trim(), data.customRequest?.trim() || undefined);

      // Show success state instead of immediately closing
      setIsSubmitted(true);

      // Auto-close after 4 seconds (giving user time to read)
      setTimeout(() => {
        handleClose();
      }, 4000);
    } catch (error) {
      logger.error("ContactModal - Submission error", { error });
      // Form error handling is managed by react-hook-form
    }
  };

  const handleClose = () => {
    reset();
    setIsSubmitted(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} placement="center" size="lg" onClose={handleClose}>
      <ModalContent className="p-2">
        {!isSubmitted ? (
          // Form state
          <form onSubmit={handleSubmit(onSubmitForm)}>
            <ModalHeader className="flex flex-col gap-2 pb-4">
              <div className="flex items-center gap-4">
                <PersonFill className="text-primary text-2xl" />
                <div>
                  <h3 className="text-2xl font-bold">Get in Touch</h3>
                  <p className="text-base text-default-500">
                    {recommendationName} - {actionName}
                  </p>
                </div>
              </div>
            </ModalHeader>
            <ModalBody className="py-6">
              <div className="space-y-6">
                <p className="text-base text-default-600 leading-relaxed">
                  We&apos;d love to help you with{" "}
                  <span className="font-medium">{recommendationName}</span>.
                  Your questions and feedback are always welcome - please share
                  your email and we&apos;ll be in touch soon!
                </p>
                <Input
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus // Acceptable for modal context - improves UX
                  classNames={{
                    input: "text-lg",
                    inputWrapper: "h-14",
                  }}
                  endContent={
                    <EnvelopeFill className="text-default-400 text-xl" />
                  }
                  errorMessage={errors.email?.message}
                  isInvalid={!!errors.email}
                  label="Email Address"
                  labelPlacement="outside"
                  placeholder="your.email@company.com"
                  size="lg"
                  type="email"
                  variant="bordered"
                  {...register("email")}
                />
                {showOnboardingNote && (
                  <div className="bg-blue-500/5 border-l-4 border-blue-400 p-4 rounded">
                    <p className="text-sm text-blue-100">
                      <strong>Note:</strong> Your responses will be included
                      with this request to help us provide you with the most
                      relevant assistance.
                    </p>
                  </div>
                )}

                {/* GDPR Privacy Notice */}
                <div className="bg-gray-500/5 border-l-4 border-gray-400 p-4 rounded">
                  <p className="text-xs text-gray-400">
                    <strong>Privacy Notice:</strong> Your email address will be
                    used solely to respond to your request. We will not share
                    your information with third parties or use it for marketing
                    purposes. Your data is processed securely and you can
                    request its deletion at any time by contacting us.
                  </p>
                </div>

                <Textarea
                  classNames={{
                    input: "min-h-[100px]",
                  }}
                  errorMessage={errors.customRequest?.message}
                  isInvalid={!!errors.customRequest}
                  label="Custom Request (Optional)"
                  labelPlacement="outside"
                  placeholder="Tell us more about your specific needs or questions..."
                  size="lg"
                  variant="bordered"
                  {...register("customRequest")}
                />
              </div>
            </ModalBody>
            <ModalFooter className="gap-4 pt-6">
              <Button
                color="danger"
                size="lg"
                variant="light"
                onPress={handleClose}
              >
                Cancel
              </Button>
              <Button
                className={cn("font-medium px-8")}
                color="primary"
                isLoading={isSubmitting}
                size="lg"
                type="submit"
              >
                {isSubmitting ? "Submitting..." : "Contact Me"}
              </Button>
            </ModalFooter>
          </form>
        ) : (
          // Success state
          <>
            <ModalHeader className="flex flex-col gap-2 pb-4">
              <div className="flex items-center gap-4">
                <CheckCircleFill className="text-success text-3xl" />
                <div>
                  <h3 className="text-2xl font-bold">Thank You!</h3>
                  <p className="text-base text-default-500">
                    Request Submitted
                  </p>
                </div>
              </div>
            </ModalHeader>
            <ModalBody className="py-6">
              <div className="space-y-6">
                <p className="text-base text-default-600 leading-relaxed">
                  Thank you for reaching out about{" "}
                  <span className="font-medium">{recommendationName}</span>! We
                  genuinely appreciate your interest and questions.
                </p>
                <p className="text-base text-default-600 leading-relaxed">
                  We&apos;ve received your request and will get back to you at{" "}
                  <span className="font-medium text-primary">{email}</span>{" "}
                  within the next 24 hours.
                </p>
                <p className="text-sm text-default-500 text-center">
                  This window will close automatically in 4 seconds...
                </p>
              </div>
            </ModalBody>
            <ModalFooter className="pt-6">
              <Button
                className={cn("w-full font-medium")}
                color="primary"
                size="lg"
                variant="light"
                onPress={handleClose}
              >
                Close
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
